import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import {
  AppendTextToChatNodeResponse,
  AppendTextToIdeaNodeResponse,
} from "../types/websocket_types";
import { SendJsonMessage } from "react-use-websocket/dist/lib/types";
import { ReadyState } from "react-use-websocket";
import {
  _appendTextToIdeaNode,
  findNodeWithIdPath,
  pathSeparatorString,
  stringPathForNodeId,
} from "./store_utils";
import { ChatNode, IdeaNode, SummarizationMode } from "./shared_types";
import {
  WS_CONSTANTS,
  WebSocketError,
  toError,
  waitForConnectionMultiAttempt,
} from "../utils/storeOnDemandReconnectWebsocketUtils";
import { createEphemeralWsSlice } from "./ephemeralWsSlice";
import {
  EphemeralWsRequest,
  EphemeralWsRequestOptions,
  startEphemeralSummarySessionRequest,
} from "@sharpana/shared/utils/EphemeralWsRequest";

export interface SessionMetadata {
  targetUrl: string;
  sessionId: string;
  targetContent: string;
  resourceId: string;
  ytVideoId?: string;
  contentDuration?: string;
  slugParam?: string;
}

export type State = {
  rootNode?: IdeaNode | undefined;
  focusedNodeIdPath?: string | undefined;
  nodeToggleStatus: Record<string, boolean>;

  chatPaneOpen: boolean;
  chatPaneLoading: boolean;
  chatPaneNodes: Array<ChatNode>;
  chatPaneStreamingFinished_UsedInTesting: boolean;

  _sendJsonMessage?: SendJsonMessage | undefined;
  reconnectWs?: (() => void) | undefined;
  sessionMetadata?: SessionMetadata | undefined;
  wsReadyState?: ReadyState | undefined;
  tabId: string;
  showDebuggingUI: boolean;
  summarizationMode?: SummarizationMode;
  activeConnectionPromise: Promise<void> | null;

  ephemeralRequests: Map<string, any>;
};

type Actions = {
  setRootNode: (newRootNode: IdeaNode) => void;
  setFocusedNodeIdPath: (newFocusedNodeIdPath: string) => void;
  setFocusNodeBackToParent: () => void;

  setChatPaneOpen: (chatPaneOpen: boolean) => void;
  setChatPaneLoading: (chatPaneLoading: boolean) => void;
  appendTextToChatPaneNode: (
    appendTextToChatNodeResponse: AppendTextToChatNodeResponse
  ) => void;
  submitChatPaneNewUserUtterance: (
    utterance: string,
    associatedText?: string
  ) => void;

  setToggleNode: (nodeIdPath: string) => void;
  expandToggleNode: (nodeIdPath: string) => void;
  collapseToggleNode: (nodeIdPath: string) => void;
  collapseAllToggles: () => void;

  appendTextToIdeaNode: (
    appendTextToIdeaNodeResponse: AppendTextToIdeaNodeResponse
  ) => void;
  addChildrenNodesAtNode: (
    parentNodePath: string,
    childrenNodes: Array<IdeaNode>
  ) => void;

  setSendJsonMessage: (newSendJsonMessage: SendJsonMessage) => void;
  setReconnectWs: (reconnectWs: () => void) => void;
  setWsReadyState: (newReadyState: ReadyState) => void;
  setSessionMetadata: (newSessionMetadata: SessionMetadata) => void;
  setTabId: (newTabId: string) => void;
  setShowDebuggingUI: (newValue: boolean) => void;
  setSummarizationMode: (newSummarizationMode: SummarizationMode) => void;

  // Legacy - will be deprecated
  sendJsonMessage: (payload: object) => void;

  // New ephemeral WebSocket methods
  startRequest: <TIn, TOut>(
    payload: TIn,
    wsUrl?: string,
    token?: string
  ) => EphemeralWsRequest<TIn, TOut>;
  cancelRequest: (requestId: string) => void;
  getRequestLastMessage: (requestId: string) => any;
  getRequestState: (requestId: string) => string;
  getRequestMessages: (requestId: string) => any[];
};

// Get current store state in a type-safe way
function getStoreState(): State & Actions {
  return useIdeasNodeStore.getState();
}

// Safely update store state
function setStoreState(updates: Partial<State>) {
  useIdeasNodeStore.setState(updates);
}

export const useIdeasNodeStore = create<State & Actions>()(
  immer((set, get) => ({
    // Attach reusable Ephemeral WS slice first
    // Type cast to satisfy overload with store param
    ...(createEphemeralWsSlice as any)(set, get, undefined),
    rootNode: undefined,
    focusedNodeIdPath: undefined,
    nodeToggleStatus: {},

    chatPaneOpen: false,
    chatPaneLoading: false,
    chatPaneNodes: [],
    chatPaneStreamingFinished_UsedInTesting: false,

    _sendJsonMessage: undefined,
    reconnectWs: undefined,
    sessionMetadata: undefined,
    wsReadyState: undefined,
    tabId: "",
    showDebuggingUI: false,
    // summarizationMode: "threadInsights", // Default to threadInsights mode
    // summarizationMode: "threadTimeline", // Default to threadInsights mode
    summarizationMode: null, // Default to threadInsights mode
    activeConnectionPromise: null,

    setSendJsonMessage: (newSendJsonMessage: SendJsonMessage) => {
      set({ _sendJsonMessage: newSendJsonMessage });
    },

    setReconnectWs: (reconnectWs: () => void) => {
      set({ reconnectWs });
    },

    setWsReadyState: (newReadyState: ReadyState) => {
      set({ wsReadyState: newReadyState });
    },

    setSessionMetadata: (newSessionMetadata: SessionMetadata) => {
      set((state) => {
        state.sessionMetadata = newSessionMetadata;
      });
    },
    setTabId: (newTabId: string) => {
      set((state) => {
        state.tabId = newTabId;
      });
    },
    setShowDebuggingUI: (newValue: boolean) => {
      set((state) => {
        state.showDebuggingUI = newValue;
      });
    },

    sendJsonMessage: async (payload: object) => {
      const { _sendJsonMessage } = getStoreState();
      if (!_sendJsonMessage) {
        throw new WebSocketError(
          "No sendJsonMessage function available",
          "CONFIGURATION"
        );
      }

      function doSend(pl: object) {
        const { sessionMetadata, summarizationMode } = getStoreState();
        // Strip out any nested sessionMetadata key that a caller might have passed
        const { sessionMetadata: _omit, ...plWithoutMeta } = pl as any;

        const enrichedPayload = {
          ...plWithoutMeta,
          ...sessionMetadata,
          summarization_mode: summarizationMode,
        };

        console.log("Sending message:", enrichedPayload);
        try {
          const result = _sendJsonMessage(enrichedPayload);
          // Handle both Promise and non-Promise returns
          if (result instanceof Promise) {
            return result.catch((err) => {
              throw new WebSocketError(
                `Failed to send message: ${toError(err).message}`,
                "SEND_FAILED"
              );
            });
          }
          return result;
        } catch (err) {
          throw new WebSocketError(
            `Failed to send message: ${toError(err).message}`,
            "SEND_FAILED"
          );
        }
      }

      // If already open, send immediately
      if (getStoreState().wsReadyState === ReadyState.OPEN) {
        return doSend(payload);
      }

      // Create AbortController for cleanup
      const abortController = new AbortController();

      try {
        // Attempt connection with retries
        await waitForConnectionMultiAttempt(
          WS_CONSTANTS.MAX_RECONNECT_ATTEMPTS,
          abortController.signal,
          getStoreState,
          setStoreState
        );

        // Verify we're actually connected
        if (getStoreState().wsReadyState !== ReadyState.OPEN) {
          throw new WebSocketError(
            "WebSocket is not open after connection attempt",
            "INVALID_STATE"
          );
        }

        // Send the message
        return await doSend(payload);
      } catch (err) {
        const error = toError(err);
        console.error("Failed to send message:", error);
        throw error;
      } finally {
        abortController.abort();
      }
    },

    setSummarizationMode: (newSummarizationMode: SummarizationMode) => {      
      set((state) => {
        state.summarizationMode = newSummarizationMode;
      });
    },

    setChatPaneOpen: (chatPaneOpen: boolean) => {
      set((state) => {
        state.chatPaneOpen = chatPaneOpen;
      });
    },
    setChatPaneLoading: (chatPaneLoading: boolean) => {
      set((state) => {
        state.chatPaneLoading = chatPaneLoading;
      });
    },
    appendTextToChatPaneNode: (
      appendTextToChatNodeResponse: AppendTextToChatNodeResponse
    ) => {
      // console.log("appendTextToChatPaneNode called with:", appendTextToChatNodeResponse);

      set((state) => {
        if (
          appendTextToChatNodeResponse.speaker === "assistant" ||
          !appendTextToChatNodeResponse.speaker
        ) {
          // If the speaker is explicitly "assistant" or not set (which means by default it's the assistant/LLM), we are done loading/waiting for LLM to start answering
          state.chatPaneLoading = false;
        }

        const existingNode = state.chatPaneNodes.filter(
          (n) => n.id == appendTextToChatNodeResponse.node_id_path
        );
        if (!appendTextToChatNodeResponse.text_to_append) {
          console.log("Early return: text_to_append is empty or null");
          return; // If text_to_append is empty or null, we exit early, otherwise we can end up appending "null" as a string
        }

        if (existingNode && existingNode.length) {
          // console.log("Appending to existing node:", existingNode[0]);
          existingNode[0].text =
            existingNode[0].text + appendTextToChatNodeResponse.text_to_append;
        } else {
          const newChatNode = {
            id: appendTextToChatNodeResponse.node_id_path,
            speaker: appendTextToChatNodeResponse.speaker
              ? appendTextToChatNodeResponse.speaker
              : "assistant",
            text: appendTextToChatNodeResponse.text_to_append,
            associated_content_text:
              appendTextToChatNodeResponse.associated_content_text,
          };
          console.log("Creating new chat node:", newChatNode);
          state.chatPaneNodes.push(newChatNode);
        }
      });
    },

    setRootNode: (newRootNode: IdeaNode) => {
      set((state) => {
        state.rootNode = newRootNode;

        if (state.focusedNodeIdPath == undefined) {
          state.focusedNodeIdPath = stringPathForNodeId(
            state.focusedNodeIdPath,
            newRootNode.id
          );
        }
      });
    },
    setFocusedNodeIdPath: (newFocusedNodeIdPath: string) => {
      set((state) => {
        state.focusedNodeIdPath = newFocusedNodeIdPath;
      });
    },
    setFocusNodeBackToParent: () => {
      set((state) => {
        const re = new RegExp(pathSeparatorString);
        const _nodeIds = state.focusedNodeIdPath?.split(re);
        const nodeIds = _nodeIds?.filter((el) => el !== "");
        nodeIds?.pop();

        if (nodeIds && nodeIds.length) {
          const newPath = nodeIds.map((el) => String(el) + "::").join("");
          state.focusedNodeIdPath = newPath;
        }
      });
    },
    setToggleNode: (nodeIdPath: string) => {
      set((state) => {
        if (nodeIdPath in state.nodeToggleStatus) {
          state.nodeToggleStatus[nodeIdPath] =
            !state.nodeToggleStatus[nodeIdPath];
        } else {
          state.nodeToggleStatus[nodeIdPath] = true;
        }
      });
    },
    expandToggleNode: (nodeIdPath: string) => {
      set((state) => {
        state.nodeToggleStatus[nodeIdPath] = true;
      });
    },
    collapseToggleNode: (nodeIdPath: string) => {
      set((state) => {
        state.nodeToggleStatus[nodeIdPath] = false;
      });
    },
    collapseAllToggles: () => {
      set((state) => {
        state.nodeToggleStatus = {};
      });
    },
    appendTextToIdeaNode: (
      appendTextToIdeaNodeResponse: AppendTextToIdeaNodeResponse
    ) => {
      set((state) => {
        _appendTextToIdeaNode(state, appendTextToIdeaNodeResponse);
      });
    },

    addChildrenNodesAtNode: (
      parentNodePath: string,
      childrenNodes: Array<IdeaNode>
    ) => {
      set((state) => {
        const parentNode = findNodeWithIdPath(
          state.rootNode as IdeaNode,
          parentNodePath
        );

        if (parentNode) {
          if (
            parentNode.ordered_children_nodes &&
            parentNode.ordered_children_nodes.length
          ) {
            parentNode.ordered_children_nodes?.push(...childrenNodes);
          } else {
            // console.log(`Will set children nodes to`);
            // console.log(childrenNodes);
            parentNode.ordered_children_nodes = childrenNodes;
          }
        }
      });
    },
    submitChatPaneNewUserUtterance: (
      utterance: string,
      associatedText?: string
    ) => {
      const store = getStoreState();

      const utterance_id = `${Date.now().toString()}:@:${Math.random()
        .toString()
        .substr(2, 6)}`;

      // Add user message to chat pane
      store.appendTextToChatPaneNode({
        node_id_path: `user-${utterance_id}`,
        node_type: "chat",
        text_to_append: utterance,
        speaker: "user",
        associated_content_text: associatedText,
        research_session_id: "",
      });

      // Send message to websocket
      store.sendJsonMessage({
        action: "user_freeform_query",
        associated_text: associatedText,
        parent_node_id_path: `speaker-${utterance_id}`,
        user_query: utterance,
      });

      // Open chat pane
      store.setChatPaneOpen(true);
      store.setChatPaneLoading(true);
    },

    submitChatPaneNewUserUtteranceEphemeral: (
      utterance: string,
      associatedText?: string
    ) => {
      const utterance_id = `${Date.now().toString()}:@:${Math.random()
        .toString()
        .substr(2, 6)}`;

      // Add user message to chat pane
      getStoreState().appendTextToChatPaneNode({
        node_id_path: `user-${utterance_id}`,
        node_type: "chat",
        text_to_append: utterance,
        speaker: "user",
        associated_content_text: associatedText,
        research_session_id: "",
      });

      // Send message via ephemeral WebSocket using centralized approach
      const { sessionMetadata, summarizationMode } = get();

      // streaming flag for tests (load testing) - re-sets to false when streaming begins
      set((draft) => {
        draft.chatPaneStreamingFinished_UsedInTesting = false;
      });

      const payload = {
        action: "user_freeform_query",
        associated_text: associatedText || "",
        parent_node_id_path: `speaker-${utterance_id}`,
        user_query: utterance,
        // Include session metadata
        ...sessionMetadata,
        summarization_mode: summarizationMode,
      };

      const request = startEphemeralSummarySessionRequest(payload, 90000);

      // Subscribe to request changes to handle streaming messages
      request.subscribe((req) => {
        // console.log(
        //   "Request state changed:",
        //   req.state,
        //   "Last message:",
        //   req.lastMessage,
        //   "Messages count:",
        //   req.messages.length
        // );

        // Handle new messages
        if (req.lastMessage && req.messages.length > 0) {
          const msg = req.lastMessage as any;
          // console.log("Processing message:", msg);
          if (msg.response_type === "append_text_to_chat_pane_node") {
            // console.log("Appending text to chat pane:", msg.text_to_append);
            getStoreState().appendTextToChatPaneNode({
              node_id_path: `speaker-${utterance_id}`,
              node_type: "chat",
              text_to_append: msg.text_to_append || "",
              speaker: "assistant",
              associated_content_text: "",
              research_session_id: "",
            });
          }
        }

        // Handle request completion
        if (req.state === "finished" || req.state === "error") {
          console.log("Request completed with state:", req.state);
          getStoreState().setChatPaneLoading(false);

          // Only set flag to true on successful completion, not on error
          if (req.state === "finished") {
            set((draft) => {
              draft.chatPaneStreamingFinished_UsedInTesting = true;
            });
          }
        }
      });

      // Open chat pane
      getStoreState().setChatPaneOpen(true);
      getStoreState().setChatPaneLoading(true);
      console.log("Chat pane opened and loading set to true");

      return request;
    },

    // ====  Ephemeral WS helpers now delegate to slice and use centralized URL handling  ====
    startRequest: <TIn, TOut>(payload: TIn, wsUrl?: string) => {
      const { sessionMetadata, summarizationMode, startEphemeralRequest } =
        get();

      // Enrich payload with session metadata like the old sendJsonMessage did
      // Set required backend fields explicitly to avoid spreading issues
      const enrichedPayload = {
        ...payload,
        ...sessionMetadata,
        summarization_mode: summarizationMode,
      } as TIn;

      console.log("Ephemeral WebSocket - Sending message:", enrichedPayload);
      console.log("Original payload:", payload);
      console.log("Session metadata:", sessionMetadata);

      // If wsUrl is provided, use it, otherwise use the standard summary session URL
      return wsUrl
        ? startEphemeralRequest<TIn, TOut>({
            url: wsUrl,
            payload: enrichedPayload,
            timeoutMs: 90000,
          })
        : startEphemeralSummarySessionRequest<TIn, TOut>(
            enrichedPayload,
            90000
          );
    },
    cancelRequest: (requestId: string) =>
      get().cancelEphemeralRequest(requestId),
    getRequestLastMessage: (requestId: string) =>
      get().getLastMessage(requestId),
    getRequestState: (requestId: string) => get().getRequestState(requestId),
    getRequestMessages: (requestId: string) =>
      get().getRequestMessages(requestId),
  }))
);
